/**
 * GridRow Component - Single row in the sequencer grid
 * Contains instrument controls and a row of cells
 */

import { useCallback, useState } from 'react';
import { GridCell } from './GridCell';
import { InstrumentSelector } from './InstrumentSelector';
import { InstrumentParameters } from './InstrumentParameters';
import { VolumeSlider } from './VolumeSlider';
import { MuteButton } from './MuteButton';
import { EffectsPanel } from './EffectsPanel';
import { NotePickerContextMenu } from './NotePickerContextMenu';
import { ConfirmPopup } from '../common/ConfirmPopup';
import type { GridRowProps } from '../../types/grid';
import type { InstrumentParameters as IParams } from '../../types/instruments';

/**
 * GridRow component
 * Renders a complete row with:
 * - Instrument selector
 * - Mute button
 * - Remove button
 * - Volume slider
 * - Array of grid cells
 * 
 * @param props - Row properties
 * @returns GridRow component
 */
export function GridRow({
  row,
  channel,
  steps,
  currentStep,
  gridState,
  noteState,
  effectsChain = [],
  effectsPanelExpanded = false,
  onCellToggle,
  onNoteChange,
  onInstrumentChange,
  onParametersChange,
  onParametersEnabledChange,
  onVolumeChange,
  onMuteToggle,
  onRemoveRow,
  onEffectsChange,
  onEffectsPanelToggle,
}: GridRowProps) {
  const [isRemoving, setIsRemoving] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [isParametersExpanded, setIsParametersExpanded] = useState(false);
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean;
    step: number;
    note: string;
  } | null>(null);

  /**
   * Handle row removal - show confirmation popup
   */
  const handleRemove = useCallback(() => {
    setShowRemoveConfirm(true);
  }, []);

  /**
   * Perform row removal with animation after confirmation
   */
  const handleConfirmRemove = useCallback(() => {
    setShowRemoveConfirm(false);
    if (isRemoving) return;
    setIsRemoving(true);
    setTimeout(() => {
      onRemoveRow();
    }, 400);
  }, [onRemoveRow, isRemoving]);

  /**
   * Handle instrument change
   */
  const handleInstrumentChange = useCallback(
    (instrument: import('../../types/composition').InstrumentType, parameters?: import('../../types/instruments').InstrumentParameters) => {
      onInstrumentChange(instrument, parameters);
    },
    [onInstrumentChange]
  );

  /**
   * Handle cell toggle
   */
  const handleCellToggle = useCallback(
    (step: number) => {
      onCellToggle(step);
    },
    [onCellToggle]
  );

  /**
   * Handle note edit - shows context menu on desktop
   */
  const handleNoteEdit = useCallback(
    (step: number, event?: React.MouseEvent) => {
      const currentNote = noteState[step] || 'C4';
      
      // If we have mouse event, show context menu (desktop)
      if (event) {
        setContextMenu({
          visible: true,
          step,
          note: currentNote,
        });
      }
    },
    [noteState]
  );

  /**
   * Handle note selection from context menu
   */
  const handleNoteSelect = useCallback(
    (note: string) => {
      if (contextMenu) {
        onNoteChange(contextMenu.step, note);
      }
    },
    [contextMenu, onNoteChange]
  );

  /**
   * Close context menu
   */
  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);


  /**
   * Handle parameter changes
   */
  const handleParametersChange = useCallback(
    (parameters: IParams) => {
      if (onParametersChange) {
        onParametersChange(parameters);
      }
    },
    [onParametersChange]
  );

  return (
    <div style={{ width: '100%' }}>
      <div className={`grid-row ${isRemoving ? 'removing' : ''}`}>
        {/* Row Controls - Sticky to left */}
        <div className="row-controls">
          {/* Instrument Selector */}
          <InstrumentSelector
            value={channel.instrument}
            onChange={handleInstrumentChange}
            row={row}
          />

          {/* Mute Button */}
          <MuteButton muted={channel.muted} onToggle={onMuteToggle} row={row} />

          {/* Volume Slider */}
          <VolumeSlider value={channel.volume} onChange={onVolumeChange} row={row} />

          {/* Parameters Toggle Button */}
          {onParametersChange && onParametersEnabledChange && (
            <button
              className="param-settings-btn"
              onClick={() => setIsParametersExpanded(!isParametersExpanded)}
              aria-label={`${isParametersExpanded ? 'Hide' : 'Show'} sound parameters for row ${row + 1}`}
              aria-expanded={isParametersExpanded}
              title={`${isParametersExpanded ? 'Hide' : 'Show'} sound parameters`}
              type="button"
            >
              <i className="fas fa-sliders-h"></i>
            </button>
          )}

          {/* Effects Panel Toggle Button */}
          {onEffectsChange && onEffectsPanelToggle && (
            <button
              className="effects-toggle-btn-compact"
              onClick={onEffectsPanelToggle}
              aria-label={`${effectsPanelExpanded ? 'Hide' : 'Show'} effects panel for row ${row + 1}`}
              aria-expanded={effectsPanelExpanded}
              title={`${effectsPanelExpanded ? 'Hide' : 'Show'} effects panel`}
              type="button"
            >
              <i className="fas fa-magic"></i>
            </button>
          )}

          {/* Remove Row Button */}
          <button
            className="remove-row-btn"
            onClick={handleRemove}
            aria-label={`Remove row ${row + 1}`}
            type="button"
            disabled={isRemoving}
          >
            ✖
          </button>
        </div>

        {/* Notes Wrapper - Scrollable container for cells */}
        <div className="notes-wrapper">
          {Array.from({ length: steps }, (_, step) => (
            <GridCell
              key={step}
              row={row}
              step={step}
              active={gridState[step] ?? false}
              note={noteState[step] ?? null}
              isCurrentStep={step === currentStep}
              onToggle={() => handleCellToggle(step)}
              onNoteEdit={(event) => handleNoteEdit(step, event)}
              onNoteChange={(newNote) => onNoteChange(step, newNote)}
            />
          ))}
        </div>
      </div>

      {/* Instrument Parameters - Collapsible panel below the row */}
      {onParametersChange && onParametersEnabledChange && isParametersExpanded && (
        <InstrumentParameters
          instrument={channel.instrument}
          parameters={channel.parameters || { adsr: { attack: 0.01, decay: 0.1, sustain: 0.7, release: 0.1 } }}
          onChange={handleParametersChange}
          parametersEnabled={channel.parametersEnabled ?? false}
          onParametersEnabledChange={onParametersEnabledChange}
          row={row}
          isExpanded={true}
        />
      )}

      {/* Effects Panel - Collapsible panel below the row */}
      {onEffectsChange && onEffectsPanelToggle && (
        <EffectsPanel
          row={row}
          isExpanded={effectsPanelExpanded}
          onToggle={onEffectsPanelToggle}
          effectsChain={effectsChain}
          onEffectsChange={onEffectsChange}
        />
      )}

      {/* Note Picker Context Menu - Desktop only */}
      {contextMenu?.visible && (
        <NotePickerContextMenu
          currentNote={contextMenu.note}
          trackIndex={row}
          stepIndex={contextMenu.step}
          onNoteSelect={handleNoteSelect}
          onClose={handleCloseContextMenu}
        />
      )}

      {/* Remove row confirmation */}
      <ConfirmPopup
        isOpen={showRemoveConfirm}
        title="Delete Track"
        message={`Remove row ${row + 1}? This will delete all notes and settings for this track.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        icon="fa-solid fa-trash"
        onConfirm={handleConfirmRemove}
        onCancel={() => setShowRemoveConfirm(false)}
      />
    </div>
  );
}
